import React, { useEffect, useState, useMemo } from 'react';
import { ArrowDownIcon, LocationIcon, FilterIcon } from '../assets/icons';
import FilterBadge from '../components/common/FilterBadge';
import NeighborhoodFilterModal from '../components/NeighborhoodFilterModal';
import { getMahalleData, getMahalleExcelData } from '../services/neighborhoodService';
import { Dropdown } from 'primereact/dropdown';
import Pagination from '../components/Pagination';

const TIME_PERIODS = [
    { label: 'Tümü', value: '' },
    { label: 'Bugün', value: 0 },
    { label: '1 Hafta', value: 7 },
    { label: '1 Ay', value: 30 },
    { label: '1 Yıl', value: 365 }
];

const Neighborhood = () => {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Statistics data for the progress bar
  const [statsData, setStatsData] = useState({
    total: 0,
    completed: 0,
    remaining: 0,
    district: '',
    neighborhood: '',
    timeframe: ''
  });
  
  // Calculate percentage for progress bar
  const progressPercentage = useMemo(() => {
    return Math.round((statsData.completed / statsData.total) * 100);
  }, [statsData.completed, statsData.total]);
  
  // Remaining count
  const remainingCount = useMemo(() => {
    return statsData.total - statsData.completed;
  }, [statsData.total, statsData.completed]);

  useEffect(() => {
    if (appliedFilters.district && appliedFilters.neighborhood) {
      fetchNeighborhoods();
    }
  }, [appliedFilters, selectedDays, currentPage]);

  const fetchNeighborhoods = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMahalleData({
        offset: currentPage,
        days: selectedDays,
        ilce: appliedFilters.district?.label,
        mah: appliedFilters.neighborhood?.label,
        sokak: appliedFilters.street?.join('#'),
        date: appliedFilters.date
      });

      if (response.status === 200) {
        setNeighborhoods(response.data || []);
        setTotalPages(Math.ceil(response.totalRecords / 15));
        // Update statistics
        setStatsData(prev => ({
          ...prev,
          total: response.statistic.totalSokak,
          completed: response.statistic.gidilenSokak,
          remaining: response.statistic.gidilecekSokak,
          district: appliedFilters.district?.label || '',
          neighborhood: appliedFilters.neighborhood?.label || '',
          timeframe: appliedFilters.date ? new Date(appliedFilters.date).toLocaleDateString('tr-TR') : 'Tümü'
        }));
      }
    } catch (error) {
      console.error('Failed to fetch neighborhoods:', error);
      setError('Mahalle verileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    try {
      // Get district and neighborhood names
      const ilce = appliedFilters.district?.label || '';
      const mah = appliedFilters.neighborhood?.label || '';
      
      const response = await getMahalleExcelData({
        ilce,
        mah,
        days: selectedDays
      });

      // Get the filename from the content-disposition header
      const contentDisposition = response.headers.get('content-disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = filenameMatch && filenameMatch[1] ? filenameMatch[1].replace(/['"]/g, '') : `mahalle-verileri-${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

      // Convert response to blob
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to fetch excel data:', error);
      setError('Excel verileri yüklenirken bir hata oluştu.');
    }
  };

  const handlePeriodChange = (e) => {
    setSelectedDays(e.target.value !== undefined && !isNaN(e.target.value) ? Number(e.target.value) : '');
    setCurrentPage(0);
  };

  const handleFilterChange = (filters) => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleRemoveFilter = (filterKey) => {
    const updatedFilters = { ...appliedFilters };
    delete updatedFilters[filterKey];
    setCurrentPage(0);
    setAppliedFilters(updatedFilters);
  };

  const handleRemoveSpecificFilter = (specificFilterKey) => {
    const { filterKey, valueIndex } = specificFilterKey;
    const currentValues = [...appliedFilters[filterKey]];
    currentValues.splice(valueIndex, 1);
    
    const updatedFilters = { ...appliedFilters };
    if (currentValues.length > 0) {
      updatedFilters[filterKey] = currentValues;
    } else {
      delete updatedFilters[filterKey];
    }
    
    setAppliedFilters(updatedFilters);
  };

  const handleLocationClick = (location, e) => {
    e.stopPropagation();
    // Open Google Maps with the given coordinates
    window.open(`https://www.google.com/maps?q=${location.latitude},${location.longitude}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="people-container">
        <h1 className="page-title">Mahalle Bazlı Veriler</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="people-container">
        <h1 className="page-title">Mahalle Bazlı Veriler</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!appliedFilters.district || !appliedFilters.neighborhood) {
    return (
      <div className="people-container">
        <h1 className="page-title">Mahalle Bazlı Veriler</h1>
        <div className="select-district-message">
          <p>Görüntülemek için ilçe ve mahalle seçiniz</p>
          <button className="general-filter-button" onClick={() => setFilterModalOpen(true)}>
            <span>Geniş Filtreleme</span>
            <FilterIcon color='#18964e' className="filter-icon" />
          </button>
        </div>
        <NeighborhoodFilterModal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onFilterChange={handleFilterChange}
          currentFilters={appliedFilters}
        />
      </div>
    );
  }

  return (
    <div className="people-container neighborhood-container">
      <h1 className="page-title">Mahalle Bazlı Veriler</h1>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="filter-container">
          <button className="download-excel-button" onClick={handleExcelDownload}>
            <p>Excel Çıktısı Al</p>
            <ArrowDownIcon color='white' className="filter-icon" />
          </button>
          <button className="general-filter-button" onClick={() => setFilterModalOpen(true)}>
            <span>Geniş Filtreleme</span>
            <FilterIcon color='#18964e' className="filter-icon" />
          </button>
          {/* <select value={null} onChange={() => {}}>
            {TIME_PERIODS.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select> */}
          <Dropdown value={selectedDays} onChange={handlePeriodChange} options={TIME_PERIODS} placeholder='Tarih Seçiniz' className='time-select personel-time-select' />
        </div>
      </div>

      {/* Applied Filters Display */}
      {Object.keys(appliedFilters).length > 0 && (
        <div className="applied-filters-container">
          {Object.entries(appliedFilters).map(([key, value]) => {
            if (key === 'date' && value) {
              return (
                <FilterBadge 
                  key={key}
                  filterKey={key}
                  filterValue={new Date(value).toLocaleDateString('tr-TR')}
                  filterLabel="Tarih"
                  onRemove={handleRemoveFilter}
                />
              );
            }
            
            if (Array.isArray(value) && value.length > 0) {
              return value.map((item, index) => {
                if (!item) return null;
                return (
                  <FilterBadge 
                    key={`${key}_${index}`}
                    filterKey={{ filterKey: key, valueIndex: index }}
                    filterValue={item}
                    filterLabel={key === 'street' ? 'Sokak' : ''}
                    onRemove={handleRemoveSpecificFilter}
                  />
                );
              });
            }
            
            if (value && typeof value === 'object' && !Array.isArray(value) && value.label) {
              return (
                <FilterBadge 
                  key={key}
                  filterKey={key}
                  filterValue={value.label}
                  filterLabel={key === 'district' ? 'İlçe' : 
                             key === 'neighborhood' ? 'Mahalle' : ''}
                  onRemove={handleRemoveFilter}
                />
              );
            }
            
            return null;
          })}
        </div>
      )}

      {/* Statistics Section */}
      <div className="statistics-section">
        <div 
          className='neighborhood-statistics-container'
          style={{ '--progress-percentage': `${progressPercentage}%` }}
        >
            <div>
                <div className='row'>
                    <span className='green italic'>{statsData.district},</span>
                    <span className='italic'>{statsData.timeframe}</span>
                </div>
                <p className='green'>{statsData.neighborhood} Gidilen Sokak Verisi</p>
            </div>
            <div>
                <div className='row'>
                    <span className='italic'>{statsData.total} / <span className='green italic'>{statsData.completed} (%{progressPercentage})</span></span>
                    <span className='yellow italic'>{remainingCount} sokak kaldı.</span>
                </div>
                <div className='progress-bar' />
            </div>
        </div>
        <div className="stat-card-container separated-waste-statistics">
          <div className="stat-card">
            <h3>Seçilen Mahallenin<br/>Sokak Sayısı</h3>
            <p className='stat-card-number'>{statsData.total}</p>
            <p className='stat-card-text'>sokak</p>
          </div>
          <div className="stat-card green">
            <h3>Toplam<br/>Gidilen Sokak</h3>
            <p className='stat-card-number'>{statsData.completed}</p>
            <p className='stat-card-text'>sokak</p>
          </div>
          <div className="stat-card">
            <h3>Toplam<br/>Gidilecek Sokak</h3>
            <p className='stat-card-number'>{statsData.remaining}</p>
            <p className='stat-card-text'>sokak</p>
          </div>
        </div>
      </div>
      <div className="table-container">
        <table className="neighborhood-table">
          <thead>
            <tr>
              <th>Tarih ve Saat</th>
              <th>İlçe</th>
              <th>Mahalle</th>
              <th>Sokak</th>
              <th>Apartman</th>
              <th>Sokak Bilgisi</th>
              <th>Konum</th>
            </tr>
          </thead>
          <tbody>
            {neighborhoods.map((item) => (
              <tr key={item.id} className="table-row">
                <td>{item.date ? new Date(item.date).toLocaleString('tr-TR') : '-'}</td>
                <td><span className="badge district">{item.ilce}</span></td>
                <td>{item.mahalle}</td>
                <td>{item.sokak}</td>
                <td>{item.apartman}</td>
                <td>
                  {item.gidildiMi ? (
                    <span className="badge green">Sokağa Gidildi</span>
                  ) : (
                    <span className="badge yellow">Sokağa Gidilecek</span>
                  )}
                </td>
                <td>
                  {
                    (item.konum && item.konum !== '-') ? (
                      <button 
                      className="location-button"
                      onClick={(e) => handleLocationClick(item.location, e)}
                      aria-label="Konumu Görüntüle"
                    >
                      <LocationIcon color="#18964e" />
                    </button>
                  ) : '-'
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      <NeighborhoodFilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onFilterChange={handleFilterChange}
        currentFilters={appliedFilters}
      />
    </div>
  );
};

export default Neighborhood;
